import React, { createContext, useContext, useState, useCallback, useRef, useEffect, useMemo, ReactNode } from 'react';
import { NotificationOptions, NotificationType, SnackbarLinkAction } from '@/types/notification';

interface SnackbarContextType {
  snackbarOpen: boolean;
  snackbarMessage: string;
  snackbarType: NotificationType;
  snackbarLink?: SnackbarLinkAction;
  showSuccess: (message: string, options?: NotificationOptions) => void;
  showError: (message: string, options?: NotificationOptions) => void;
  showWarning: (message: string, options?: NotificationOptions) => void;
  showInfo: (message: string, options?: NotificationOptions) => void;
  closeSnackbar: () => void;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

export const SnackbarProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarType, setSnackbarType] = useState<NotificationType>('success');
  const [snackbarLink, setSnackbarLink] = useState<SnackbarLinkAction | undefined>(undefined);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const closeSnackbar = useCallback(() => {
    setSnackbarOpen(false);
    setSnackbarLink(undefined);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const showNotification = useCallback((message: string, type: NotificationType, options?: NotificationOptions) => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    setSnackbarMessage(message);
    setSnackbarType(type);
    setSnackbarLink(options?.link);
    setSnackbarOpen(true);
    
    // Auto-dismiss after different durations based on type
    const getDuration = () => {
      if (type === 'error') return 10000;
      if (type === 'warning') return 5000;
      return 4000; // Success/Info: 4s
    };
    const duration = getDuration();
    timeoutRef.current = setTimeout(() => {
      setSnackbarOpen(false);
      setSnackbarLink(undefined);
      timeoutRef.current = null;
    }, duration);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const showSuccess = useCallback((message: string, options?: NotificationOptions) => {
    showNotification(message, 'success', options);
  }, [showNotification]);

  const showError = useCallback((message: string, options?: NotificationOptions) => {
    showNotification(message, 'error', options);
  }, [showNotification]);

  const showWarning = useCallback((message: string, options?: NotificationOptions) => {
    showNotification(message, 'warning', options);
  }, [showNotification]);

  const showInfo = useCallback((message: string, options?: NotificationOptions) => {
    showNotification(message, 'info', options);
  }, [showNotification]);

  const value = useMemo(() => ({
    snackbarOpen,
    snackbarMessage,
    snackbarType,
    snackbarLink,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    closeSnackbar
  }), [
    snackbarOpen,
    snackbarMessage,
    snackbarType,
    snackbarLink,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    closeSnackbar
  ]);

  return (
    <SnackbarContext.Provider value={value}>
      {children}
    </SnackbarContext.Provider>
  );
};

// Export hook in a separate function to use in other files
// eslint-disable-next-line react-refresh/only-export-components
export function useSnackbarContext(): SnackbarContextType {
  const context = useContext(SnackbarContext);
  if (context === undefined) {
    throw new Error('useSnackbarContext must be used within a SnackbarProvider');
  }
  return context;
}

